import { jwtDecode, JwtPayload, InvalidTokenError } from "jwt-decode";
import { isEqual } from "lodash";
import { Signal } from "@/utils/signal";
import { ACCESS_TOKEN_COOKIE_NAME } from "@/utils/auth";

export interface AuthToken {
  readonly token: string;
  readonly claims: JwtPayload;
}

export const AuthToken = {
  equals(a: AuthToken | null, b: AuthToken | null): boolean {
    return (
      (a === null && b === null) ||
      (a !== null && b !== null && a.token === b.token)
    );
  },

  safeDecode(token: string | null): AuthToken | null {
    if (token === null) return null;
    try {
      return { token, claims: jwtDecode(token) };
    } catch (error) {
      if (error instanceof InvalidTokenError) {
        return null;
      }
      throw error;
    }
  },

  hasExpired(token: AuthToken | null): boolean {
    if (token === null) return false;
    if (token.claims.exp === undefined) return true;
    return token.claims.exp * 1000 <= Date.now();
  },
};

interface AuthTokens {
  access: AuthToken | null;
  refresh: AuthToken | null;
}

const AuthTokens = {
  nil: Object.freeze<AuthTokens>({
    access: null,
    refresh: null,
  }),
  equals(a: AuthTokens, b: AuthTokens): boolean {
    return (
      AuthToken.equals(a.access, b.access) &&
      AuthToken.equals(a.refresh, b.refresh)
    );
  },
  decode(accessToken: string | null, refreshToken: string | null): AuthTokens {
    if (refreshToken === null) return AuthTokens.nil;

    const refresh = AuthToken.safeDecode(refreshToken);
    if (refresh === null) return AuthTokens.nil;

    return { access: AuthToken.safeDecode(accessToken), refresh };
  },
};

interface AppState {
  auth: AuthTokens;
  location?: string | undefined;
}

const AppState = {
  equals(
    { auth: authA, ...a }: AppState,
    { auth: authB, ...b }: AppState,
  ): boolean {
    return AuthTokens.equals(authA, authB) && isEqual(a, b);
  },
};

export interface AppHandle {
  readonly isAuthenticated: boolean;
  readonly token: AuthToken | null;
  readonly userId: string | undefined;
  revokeAccess(): void;
  revokeAll(): void;
  setTokens(access: string | null, refresh: string | null): void;
  setLocation(location: string): void;
}

export class AppSignal extends Signal<AppState> implements AppHandle {
  static readLocal(context: Window = window) {
    const access = context.localStorage.getItem(ACCESS_TOKEN_NAME);
    const refresh = context.localStorage.getItem(REFRESH_TOKEN_NAME);
    return new AppSignal({
      auth: AuthTokens.decode(access, refresh),
    });
  }

  protected shortcuit(current: AppState, next: AppState): boolean {
    return AppState.equals(current, next);
  }

  get token() {
    const {
      auth: { access, refresh },
    } = this.value;
    return access ?? refresh;
  }

  get isAuthenticated() {
    return this.token !== null;
  }

  get userId() {
    return this.token?.claims.sub;
  }

  revokeAccess() {
    this.dispatch({
      ...this.value,
      auth: { ...this.value.auth, access: null },
    });
  }

  revokeAll() {
    this.dispatch({ ...this.value, auth: AuthTokens.nil });
  }

  setTokens(access: string | null, refresh: string | null) {
    this.dispatch({
      ...this.value,
      auth: AuthTokens.decode(access, refresh),
    });
  }

  setLocation(location: string) {
    this.dispatch({ ...this.value, location });
  }

  asUserId() {
    return this.map(
      ({ auth: { access, refresh } }) => (access ?? refresh)?.claims.sub,
    );
  }

  syncLocalStorage(context: Window = window) {
    return this.map((state) => state.auth, AuthTokens.equals).subscribe(
      (auth) => writeLocalStorage(auth, context),
    );
  }

  autoExpires() {
    const schedule = (token: AuthToken | null, revoke: () => void) => {
      if (token === null) return;
      if (token.claims.exp === undefined) return;

      const millis = token.claims.exp * 1000 - Date.now();
      if (millis <= 0 || millis > 0xfffffff) return;

      return setTimeout(revoke, millis);
    };

    let timeout1 = schedule(this.value.auth.access, () => this.revokeAccess());
    let timeout2 = schedule(this.value.auth.refresh, () => this.revokeAll());
    return this.map((state) => state.auth, AuthTokens.equals).subscribe({
      next: ({ access, refresh }) => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        timeout1 = schedule(access, () => this.revokeAccess());
        timeout2 = schedule(refresh, () => this.revokeAll());
      },
      complete: () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
      },
    });
  }

  cleanStaleTokens(context: Window = window) {
    const {
      auth: { access, refresh },
    } = this.value;

    if (AuthToken.hasExpired(refresh)) {
      this.revokeAll();
    } else if (AuthToken.hasExpired(access)) {
      this.revokeAccess();
    }

    writeLocalStorage(this.value.auth, context);
  }

  detectCookieMismatch(context: Window = window) {
    const access = this.value.auth.access?.token;
    const cookie = getCookies(context)[ACCESS_TOKEN_COOKIE_NAME];
    return access !== undefined && cookie === undefined;
  }
}

function writeLocalStorage(
  { access, refresh }: AuthTokens,
  context: Window = window,
) {
  if (access) {
    context.localStorage.setItem(ACCESS_TOKEN_NAME, access.token);
    if (access.claims.exp !== undefined) {
      setCookie(
        ACCESS_TOKEN_COOKIE_NAME,
        access.token,
        access.claims.exp * 1000,
        true,
        "/",
        context,
      );
    }
  } else {
    context.localStorage.removeItem(ACCESS_TOKEN_NAME);
    clearCookie(ACCESS_TOKEN_COOKIE_NAME, context); // but clients can clear their own cookies
  }

  if (refresh) {
    context.localStorage.setItem(REFRESH_TOKEN_NAME, refresh.token);
  } else {
    context.localStorage.removeItem(REFRESH_TOKEN_NAME);
  }
}

// Cookie domains to try, broadest first, ending in `undefined` for "no domain
// attribute" (host-only) — the fallback for hosts that accept no domain at all,
// such as `localhost` and bare IPs.
//   app.aqora.io -> ["io", "aqora.io", "app.aqora.io", undefined]
export function cookieDomains(hostname: string): (string | undefined)[] {
  const labels = hostname.split(".");
  return [
    ...labels.map((_, index) =>
      labels.slice(labels.length - 1 - index).join("."),
    ),
    undefined,
  ];
}

export function getCookies(context: Window = window): Record<string, string> {
  const cookies: Record<string, string> = {};
  for (const cookie of context.document.cookie.split(";")) {
    const [cookieName, cookieValue] = cookie.trim().split("=", 2);
    cookies[cookieName] = cookieValue;
  }
  return cookies;
}

function writeCookie(
  name: string,
  value: string,
  expiry: number,
  secure: boolean,
  path: string | undefined,
  domain: string | undefined,
  context: Window,
): void {
  const parts = [`${name}=${encodeURIComponent(value)}`];
  if (domain !== undefined) {
    parts.push(`domain=${domain}`);
  }
  if (path !== undefined) {
    parts.push(`path=${path}`);
  }
  if (secure) {
    parts.push("secure");
  }
  const expires = new Date(expiry);
  parts.push(`expires=${expires.toUTCString()}`);
  context.document.cookie = parts.join("; ");
}

// The cookie domain must match the backend's (the registrable domain / eTLD+1),
// or the cookie set by the server won't be overwritten here or cleared on
// logout. Browsers reject a cookie whose domain is a public suffix, so walking
// broadest-first the first domain that sticks *is* the registrable domain — the
// same value the backend derives from the PSL in `AuthConfig::top_domain`
// (backend/config/src/auth.rs). Using the real cookie as the probe means we
// never need a public suffix list on the client.
// Probe technique adapted from github.com/Aaronius/get-apex-domain (MIT).
function setCookie(
  name: string,
  value: string,
  expiry: number,
  secure: boolean,
  path?: string | undefined,
  context: Window = window,
): void {
  const encoded = encodeURIComponent(value);
  for (const domain of cookieDomains(context.location.hostname)) {
    writeCookie(name, value, expiry, secure, path, domain, context);
    if (getCookies(context)[name] === encoded) return;
  }
}

// No need to work out which domain the server used — expiring a cookie that
// isn't there is a no-op, so expire every candidate. `path` must be "/" to
// match how the cookie was set (both here and by the backend); a delete whose
// path doesn't match is silently ignored on any page below the root.
function clearCookie(name: string, context: Window = window): void {
  for (const domain of cookieDomains(context.location.hostname)) {
    writeCookie(name, "", 0, true, "/", domain, context);
  }
}

const ACCESS_TOKEN_NAME = "aqora_access_token";
const REFRESH_TOKEN_NAME = "aqora_refresh_token";
